import React, { Fragment, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  all,
  compose,
  equals,
  flatten,
  intersperse,
  isEmpty,
  map,
  pipe,
  prop,
  reduce,
  reject,
  sortBy,
  toLower,
  values,
} from "ramda";
import { deduplicate, sortByName } from "../../../util/mediaUtil";
import { isBlank } from "../../../util/ext";
import { useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronsRight } from "@fortawesome/pro-regular-svg-icons";

import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";

import ParentMenuItem from "../content-groups/card/ParentMenuItem";

const PADDING = 30;

const useStyles = makeStyles(theme => ({
  root: {
    fontSize: "1.8rem",
    lineHeight: "26px",
    fontWeight: "bold",
    zIndex: 10,
    background: "white",
    paddingTop: 20,
  },

  divider: {
    marginLeft: PADDING,
    marginRight: PADDING,
    marginBottom: 8,
  },

  icon: {
    fontSize: "0.8rem",
  },

  paper: {
    marginLeft: 10,
    boxShadow:
      "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
    borderRadius: 10,
  },

  secondary: {
    fontSize: "1.2rem",
    fontWeight: 400,
    lineHeight: "16px",
  },

  subheader: {
    fontWeight: 700,
    fontSize: "1.4rem",
    lineHeight: "20px",
    color: theme.palette.primary.darker,
    paddingBottom: 10,
    paddingTop: 25,
  },

  subheaderGutters: {
    paddingLeft: PADDING,
    paddingRight: PADDING,
  },
}));

const buildBreadcrumbs = parent => {
  if (equals(parent.mediaType, "module")) {
    return [];
  } else {
    return pipe(
      flatten,
      reject(isBlank),
      map(parent =>
        reject(isBlank, [
          prop("name", parent.parentModules?.[0]),
          prop("name", parent),
        ]),
      ),
      reject(isBlank),
      // sort by first name in list, i.e. top breadcrumb
      sortBy(compose(toLower, prop(0))),
    )([parent.anthology, parent.parentModules]);
  }
};

const IncludedInMenu = ({
  anchorEl,
  child,
  handleClose,
  parents,
  open,
  setLoading,
  anchorOrigin = {
    vertical: "center",
    horizontal: "center",
  },
  transformOrigin = {
    vertical: "top",
    horizontal: "center",
  },
}) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [subheaders, setSubheaders] = useState({
    unorganized: { label: "Unorganized", parents: [] },
    organized: { label: "Organized", parents: [] },
  });

  useEffect(() => {
    const subheaders = pipe(
      flatten,
      deduplicate,
      sortByName,
      map(parent => ({
        ...parent,
        breadcrumbs: buildBreadcrumbs(parent),
        handleClick: () =>
          history.push(`/creator/${parent.mediaType}s/${parent.id}`, {
            label: "Back to All Content",
            previousLocation: location.pathname + location.search,
          }),
      })),
      reduce(
        (subheaders, parent) => {
          if (
            equals(parent.mediaType, "collection") &&
            all(isBlank, [parent.ancestorModules, parent.parentModules])
          ) {
            subheaders.unorganized.parents.push(parent);
          } else {
            subheaders.organized.parents.push(parent);
          }
          return subheaders;
        },
        {
          unorganized: { label: "Unorganized", parents: [] },
          organized: { label: "Organized", parents: [] },
        },
      ),
      // remove empty root node
      reject(subheader => isEmpty(subheader.parents)),
    )([parents]);

    setSubheaders(subheaders);
  }, [parents, history, location]);

  return (
    <Popover
      onClick={e => e.stopPropagation()}
      elevation={0}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      classes={{
        paper: classes.paper,
      }}
    >
      <List
        color="inherit"
        id={`menu-for-parents-of-${child.id}`}
        subheader={
          <ListSubheader
            color="inherit"
            classes={{
              root: classes.root,
              gutters: classes.subheaderGutters,
            }}
          >
            Included In
          </ListSubheader>
        }
      >
        {isBlank(parents) ? (
          <ListItem
            classes={{ gutters: classes.subheaderGutters }}
            style={{ fontStyle: "italic" }}
          >
            <Typography>
              This content group does not belong to any other content group.
            </Typography>
          </ListItem>
        ) : (
          values(subheaders).map(subheader => (
            <Fragment key={subheader.label}>
              <ListSubheader
                disableSticky
                classes={{
                  root: classes.subheader,
                  gutters: classes.subheaderGutters,
                }}
              >
                {subheader.label}
              </ListSubheader>
              <Divider className={classes.divider} />
              {map(
                parent => (
                  <ParentMenuItem
                    disableTypography
                    key={parent.id}
                    parent={parent}
                    secondary={
                      !isEmpty(parent.breadcrumbs) && (
                        <Grid
                          container
                          direction="column"
                          className="secondary-container"
                        >
                          {map(
                            breadcrumbs => (
                              <Grid
                                key={Math.random()}
                                item
                                container
                                alignItems="baseline"
                                style={{ gap: 5 }}
                              >
                                {pipe(
                                  map(breadcrumb => (
                                    <Typography key={Math.random()}>
                                      {breadcrumb}
                                    </Typography>
                                  )),
                                  intersperse(
                                    <FontAwesomeIcon
                                      key={Math.random()}
                                      className={classes.icon}
                                      icon={faChevronsRight}
                                    />,
                                  ),
                                )(breadcrumbs)}
                              </Grid>
                            ),
                            parent.breadcrumbs,
                          )}
                        </Grid>
                      )
                    }
                    onClick={parent.handleClick}
                    child={child}
                    setLoading={setLoading}
                  />
                ),
                subheader.parents,
              )}
            </Fragment>
          ))
        )}
      </List>
    </Popover>
  );
};

export default IncludedInMenu;
